import { navigate } from "gatsby";
import React from "react";
import styled from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import { Element } from "react-scroll";

import { media } from "src/utils/media";
import { H2Pre, H3, H4, Body2Pre, Button } from "src/components/Typography";
import { colors } from "src/styles/colors";
import { translate } from "src/locales";
import images from "src/images";

type Props = WithTranslation;

const Container = styled.div`
  min-width: 1440px;
  padding-top: 189px;
  padding-bottom: 200px;
  background-color: ${colors.bg.white.sub};
  ${media.mobile`
    min-width: auto;
    height: auto;
    padding-top: 108px;
    padding-bottom: 108px;
  `}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 1268px;
  margin: auto;
  ${media.mobile`
    flex-direction: column;
    justify-content: center;
    width: 327px;
    height: auto;
    margin: auto;
  `}
`;

const LeftGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-self: flex-start;
  ${media.mobile`
    align-self: center;
  `}
`;

const Title = styled(H2Pre)`
  color: ${colors.text.dark.title};
  text-align: left;
  ${media.mobile`
    text-align: center;
  `}
`;

const Link = styled(Button)`
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${colors.bg.black.main};
  white-space: nowrap;
  /* &:hover {
    color: ${colors.primary.hover};
  } */
  cursor: pointer;
  ${media.mobile`
    justify-content: center;
    margin-top: 16px;
    margin-bottom: 0px;
  `}
`;

const LinkIcon = styled.img`
  height: 12px;
  margin-left: 8.5px;
`;

const RightGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-end;
  ${media.mobile`
    align-items: center;
    margin-top: 52px;
  `}
`;

const SecurityItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 511px;
  margin-bottom: 60px;
  ${media.mobile`
    width: 327px;
    min-height: 68px;
    margin-bottom: 36px;
  `}
`;

const LastSecurityItem = styled(SecurityItem)`
  margin-bottom: 0px;
  ${media.mobile`
    margin-bottom: 0px;
  `}
`;

const SecurityImageBg = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  min-height: 86px;
  background-color: ${colors.primary.default};
  border-radius: 100px;
  margin-right: 28px;
  ${media.mobile`
    min-width: 60px;
    min-height: 60px;
    margin-right: 16px;
  `}
`;

const SecurityImage = styled.img`
  width: 36px;
  height: 36px;
  ${media.mobile`
    width: 24px;
    height: 24px;
  `}
`;

const SecurityContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecurityTitle = styled(H4)`
  color: ${colors.text.dark.title};
`;

const SecurityDescription = styled(Body2Pre)`
  white-space: pre;
  color: ${colors.text.dark.body};
  margin-top: 8px;
  ${media.mobile`
    white-space: normal;
    margin-top: 4px;
  `}
`;

const SecuritySection = (props: Props) => {
  const { t, i18n } = props;
  const onLink = () => {
    navigate(`/${i18n.language}/product#security`);
  };

  return (
    <Container>
      <Inner>
        <LeftGroup>
          <Title>{translate(["securitySection", "title"], t)}</Title>
          <Link onClick={onLink}>
            {translate(["securitySection", "learnMoreButton"], t)}
            <LinkIcon src={images.arrowRightSrc} srcSet={images.arrowRight} />
          </Link>
        </LeftGroup>
        <RightGroup>
          <SecurityItem>
            <SecurityImageBg>
              <SecurityImage
                src={images.multisigWalletLogoSrc}
                srcSet={images.multisigWalletLogo}
              />
            </SecurityImageBg>
            <SecurityContent>
              <SecurityTitle>
                {translate(["securitySection", "multisigWallet", "title"], t)}
              </SecurityTitle>
              <SecurityDescription>
                {translate(
                  ["securitySection", "multisigWallet", "description"],
                  t
                )}
              </SecurityDescription>
            </SecurityContent>
          </SecurityItem>
          <SecurityItem>
            <SecurityImageBg>
              <SecurityImage
                src={images.hackingGuardLogoSrc}
                srcSet={images.hackingGuardLogo}
              />
            </SecurityImageBg>
            <SecurityContent>
              <SecurityTitle>
                {translate(["securitySection", "hackingGuard", "title"], t)}
              </SecurityTitle>
              <SecurityDescription>
                {translate(
                  ["securitySection", "hackingGuard", "description"],
                  t
                )}
              </SecurityDescription>
            </SecurityContent>
          </SecurityItem>
          <LastSecurityItem>
            <SecurityImageBg>
              <SecurityImage
                src={images.authSeperationLogoSrc}
                srcSet={images.authSeperationLogo}
              />
            </SecurityImageBg>
            <SecurityContent>
              <SecurityTitle>
                {translate(["securitySection", "authSeperation", "title"], t)}
              </SecurityTitle>
              <SecurityDescription>
                {translate(
                  ["securitySection", "authSeperation", "description"],
                  t
                )}
              </SecurityDescription>
            </SecurityContent>
          </LastSecurityItem>
        </RightGroup>
      </Inner>
    </Container>
  );
};

export default withTranslation()(SecuritySection);
